/**
 * 中国象棋棋盘组件
 * 9×10棋盘，楚河汉界，传统中国象棋规则
 * 传统经典UI设计
 */

import React, { useState } from 'react';
import { XiangqiGameState, Position, XiangqiPiece, XiangqiPieceType } from '../types/game';
import { XIANGQI_PIECE_SYMBOLS, XIANGQI_PIECE_EMOJIS, XIANGQI_PIECE_NAMES } from '../types/game';
import { cn } from '../lib/utils';

interface XiangqiBoardProps {
  gameState: XiangqiGameState;
  onCellClick: (position: Position) => void;
  disabled?: boolean;
  gameMode?: 'pvp' | 'pvc';
  isZoomed?: boolean;
  onZoomToggle?: () => void;
}

const XiangqiBoard: React.FC<XiangqiBoardProps> = ({
  gameState,
  onCellClick,
  disabled = false,
  gameMode = 'pvp',
  isZoomed = false,
  onZoomToggle
}) => {
  const board = gameState.board;

  
  // 响应式棋盘尺寸配置 - 优化版本
  const getBoardSize = () => {
    // 获取屏幕信息
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
    const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 667;
    const isLandscape = screenWidth > screenHeight;

    // 电脑端优化：根据屏幕高度动态调整
    const getDesktopSize = () => {
      const maxHeight = Math.min(screenHeight - 200, 900); // 留出UI空间
      const maxWidth = Math.min(screenWidth - 100, 800);
      const useHeight = maxHeight / 848 < maxWidth / 720; // 以高度或宽度为限制

      if (useHeight) {
        const scale = maxHeight / 848;
        return {
          width: 720 * scale,
          height: maxHeight,
          margin: 68 * scale,
          cellWidth: 73.125 * scale,
          cellHeight: 79.5 * scale
        };
      } else {
        const scale = maxWidth / 720;
        return {
          width: maxWidth,
          height: 848 * scale,
          margin: 68 * scale,
          cellWidth: 73.125 * scale,
          cellHeight: 79.5 * scale
        };
      }
    };

    // 移动端优化：更好的适配
    const getMobileSize = () => {
      if (isLandscape) {
        // 横屏模式：充分利用宽度
        const availableWidth = Math.min(screenWidth - 80, 600);
        const scale = availableWidth / 480;
        return {
          width: availableWidth,
          height: 565 * scale,
          margin: 45 * scale,
          cellWidth: 48.75 * scale,
          cellHeight: 53 * scale
        };
      } else {
        // 竖屏模式：考虑手机安全区域
        const safeAreaTop = typeof window !== 'undefined' && window?.safeAreaInsets?.top || 0;
        const availableHeight = screenHeight - safeAreaTop - 250; // 预留UI空间
        const availableWidth = screenWidth - 32;
        const maxByHeight = availableHeight / 565 * 480;
        const calculatedWidth = Math.min(availableWidth, maxByHeight, 380);
        const scale = calculatedWidth / 480;
        return {
          width: calculatedWidth,
          height: 565 * scale,
          margin: 45 * scale,
          cellWidth: 48.75 * scale,
          cellHeight: 53 * scale
        };
      }
    };

    // 根据屏幕尺寸和缩放模式返回尺寸
    if (screenWidth >= 1024) {
      // 桌面端
      return isZoomed ? getDesktopSize() : {
        width: 520,
        height: 612,
        margin: 50,
        cellWidth: 52.8,
        cellHeight: 57.4
      };
    } else if (screenWidth >= 768) {
      // 平板端
      if (isZoomed) {
        const scale = Math.min((screenWidth - 60) / 600, (screenHeight - 150) / 707);
        return {
          width: 600 * scale,
          height: 707 * scale,
          margin: 57 * scale,
          cellWidth: 60.9375 * scale,
          cellHeight: 66.25 * scale
        };
      } else {
        return getMobileSize();
      }
    } else {
      // 移动端
      return getMobileSize();
    }
  };

  const { width: boardWidth, height: boardHeight, margin, cellWidth, cellHeight } = getBoardSize();
  const lineHeight = isZoomed ? '3px' : '2px';

  const getPieceSize = (): string => {
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
    const isLargeDesktop = screenWidth >= 1440;
    const isMediumDesktop = screenWidth >= 1200;
    const isSmallDesktop = screenWidth >= 1024;

    if (isZoomed) {
      if (isLargeDesktop) return 'text-4xl lg:text-4xl';
      if (isMediumDesktop) return 'text-3xl lg:text-4xl';
      if (isSmallDesktop) return 'text-2xl sm:text-3xl';
      if (screenWidth >= 768) return 'text-xl sm:text-2xl';
      return 'text-lg sm:text-xl';
    } else {
      if (isLargeDesktop) return 'text-2xl lg:text-3xl';
      if (isMediumDesktop) return 'text-xl lg:text-2xl';
      if (isSmallDesktop) return 'text-lg sm:text-xl';
      if (screenWidth >= 768) return 'text-base sm:text-lg';
      return 'text-sm sm:text-base';
    }
  };

  const getPieceStyle = (piece: XiangqiPiece, isSelected: boolean): string => {
    const baseStyle = 'flex items-center justify-center rounded-full shadow-2xl transition-all duration-300 font-bold relative cursor-pointer hover:scale-105';
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
    const isLargeDesktop = screenWidth >= 1440;
    const isMediumDesktop = screenWidth >= 1200;
    const isSmallDesktop = screenWidth >= 1024;

    // 根据屏幕尺寸动态调整棋子大小
    let sizeStyle;
    if (isZoomed) {
      if (isLargeDesktop) sizeStyle = 'w-16 h-16 lg:w-16 lg:h-16';
      else if (isMediumDesktop) sizeStyle = 'w-16 h-16 lg:w-16 lg:h-16';
      else if (isSmallDesktop) sizeStyle = 'w-16 h-16';
      else if (screenWidth >= 768) sizeStyle = 'w-14 h-14';
      else sizeStyle = 'w-10 h-10';
    } else {
      if (isLargeDesktop) sizeStyle = 'w-12 h-12 lg:w-14 lg:h-14';
      else if (isMediumDesktop) sizeStyle = 'w-12 h-12 lg:w-14 lg:h-14';
      else if (isSmallDesktop) sizeStyle = 'w-10 h-10 lg:w-12 lg:h-12';
      else if (screenWidth >= 768) sizeStyle = 'w-9 h-9';
      else sizeStyle = 'w-8 h-8';
    }

    // 根据棋子颜色和选中状态返回样式
    if (piece.player === 'red') {
      return `${baseStyle} ${sizeStyle} bg-gradient-to-br from-red-500 to-red-700 text-white border-4 border-red-800`;
    } else {
      return `${baseStyle} ${sizeStyle} bg-gradient-to-br from-gray-700 to-gray-900 text-white border-4 border-black`;
    }
  };

  const renderPiece = (piece: XiangqiPiece | null, row: number, col: number) => {
    if (!piece) return null;

    const isSelected = gameState.selectedPiece?.row === row && gameState.selectedPiece?.col === col;
    const isLastMove = gameState.moves.length > 0 &&
      gameState.moves[gameState.moves.length - 1].to.row === row &&
      gameState.moves[gameState.moves.length - 1].to.col === col;
    const isCurrentPlayerPiece = piece.player === gameState.currentPlayer;

    return (
      <div className={cn(
        getPieceStyle(piece, isSelected),
        {
          'scale-110 shadow-3xl border-4 border-yellow-400 ring-4 ring-yellow-300 ring-opacity-90': isSelected,
          'ring-2 ring-orange-400 ring-opacity-90 ring-offset-2': isLastMove && !isSelected,
          'opacity-95': !isCurrentPlayerPiece && !isSelected,
          'hover:scale-105': !disabled && isCurrentPlayerPiece
        }
      )}>
        {/* 内部装饰圆环 */}
        <div className={cn(
          "absolute inset-1 rounded-full border opacity-20",
          piece.player === 'red' ? 'border-black' : 'border-white'
        )}></div>

        {/* 棋子文字 */}
        <div className={cn(
          getPieceSize(),
          'font-serif leading-none relative z-10 select-none',
          piece.player === 'red' ? 'text-white drop-shadow-sm' : 'text-white drop-shadow-sm'
        )}>
          {XIANGQI_PIECE_SYMBOLS[piece.type][piece.player === 'red' ? 'red' : 'black']}
        </div>

        {/* 立体高光效果 */}
        <div className="absolute top-1 left-1 w-3 h-3 bg-white rounded-full opacity-50 blur-sm"></div>
      </div>
    );
  };

  // 获取最后一步的移动标记位置
  const getLastMovePosition = (): Position | null => {
    if (gameState.moves.length === 0) return null;
    const lastMove = gameState.moves[gameState.moves.length - 1];
    return lastMove.to;
  };

  const lastMovePos = getLastMovePosition();

  return (
    <div className={cn(
      "flex flex-col items-center w-full max-w-7xl mx-auto px-2 sm:px-4",
      isZoomed ? "py-4" : "py-3"
    )}>
      {/* 传统线式中国象棋棋盘 - 自适应容器 */}
      <div className="relative bg-amber-200 p-3 sm:p-6 rounded-xl shadow-2xl border-4 border-amber-900">
        {/* 棋盘容器 - 简洁背景 */}
        <div className={cn(
          "relative bg-amber-50 rounded-lg shadow-inner transition-all duration-300 mx-auto",
        )} style={{
          width: `${boardWidth}px`,
          height: `${boardHeight}px`,
          maxWidth: '100%',
          touchAction: 'manipulation', // 防止双击缩放，只允许基本手势
          WebkitUserSelect: 'none', // 防止文本选择
          WebkitTouchCallout: 'none', // 防止长按菜单
        }}>
          {/* 棋盘边框 */}
          <div className="absolute inset-2 border-4 border-amber-900 rounded pointer-events-none" style={{zIndex: 2}}></div>

          {/* 横线 - 10条，留出边距 */}
          {Array.from({ length: 10 }).map((_, rowIndex) => (
            <div
              key={`h-${rowIndex}`}
              className="absolute bg-black"
              style={{
                top: `${margin + rowIndex * cellHeight}px`,
                left: `${margin}px`,
                width: `${boardWidth - 2 * margin}px`,
                height: lineHeight,
                zIndex: 5
              }}
            ></div>
          ))}

          {/* 竖线 - 9条，中间断开 - 使用SVG绘制 */}
          <svg
            className="absolute pointer-events-none"
            style={{
              top: `${margin}px`,
              left: `${margin}px`,
              width: `${8 * cellWidth}px`,
              height: `${9 * cellHeight}px`,
              zIndex: 10
            }}
          >
            {Array.from({ length: 9 }).map((_, colIndex) => {
              const isBorder = colIndex === 0 || colIndex === 8;
              return (
                <g key={`v-svg-${colIndex}`}>
                  {/* 上半部分竖线 */}
                  <line
                    x1={colIndex * cellWidth}
                    y1={0}
                    x2={colIndex * cellWidth}
                    y2={4 * cellHeight}
                    stroke="black"
                    strokeWidth={isBorder ? "3" : "2"}
                                      />
                  {/* 下半部分竖线 */}
                  <line
                    x1={colIndex * cellWidth}
                    y1={5 * cellHeight}
                    x2={colIndex * cellWidth}
                    y2={9 * cellHeight}
                    stroke="black"
                    strokeWidth={isBorder ? "3" : "2"}
                                      />
                </g>
              );
            })}
          </svg>

          {/* 九宫格对角线 - 黑方 */}
          {/* 黑方九宫格覆盖(3,0)到(5,2)，形成完整的米字线 */}
          <svg
            className="absolute pointer-events-none"
            style={{
              top: `${margin}px`, // 第0行位置
              left: `${margin + 3 * cellWidth}px`, // 第3列位置
              width: `${2 * cellWidth}px`, // 2格间距
              height: `${2 * cellHeight}px`, // 2格间距
              opacity: 0.5
            }}
          >
            {/* 主对角线：左上到右下 (3,0) → (5,2) */}
            <line
              x1="0"
              y1="0"
              x2={`${2 * cellWidth}`}
              y2={`${2 * cellHeight}`}
              stroke="#8B7355"
              strokeWidth="2"
              strokeDasharray="6,4"
            />
            {/* 副对角线：右上到左下 (5,0) → (3,2) */}
            <line
              x1={`${2 * cellWidth}`}
              y1="0"
              x2="0"
              y2={`${2 * cellHeight}`}
              stroke="#8B7355"
              strokeWidth="2"
              strokeDasharray="6,4"
            />
          </svg>

          {/* 九宫格对角线 - 红方 */}
          {/* 红方九宫格覆盖(3,7)到(5,9)，形成完整的米字线 */}
          <svg
            className="absolute pointer-events-none"
            style={{
              top: `${margin + 7 * cellHeight}px`, // 第7行位置
              left: `${margin + 3 * cellWidth}px`, // 第3列位置
              width: `${2 * cellWidth}px`, // 2格间距
              height: `${2 * cellHeight}px`, // 2格间距
              opacity: 0.5
            }}
          >
            {/* 主对角线：左上到右下 (3,7) → (5,9) */}
            <line
              x1="0"
              y1="0"
              x2={`${2 * cellWidth}`}
              y2={`${2 * cellHeight}`}
              stroke="#8B7355"
              strokeWidth="2"
              strokeDasharray="6,4"
            />
            {/* 副对角线：右上到左下 (5,7) → (3,9) */}
            <line
              x1={`${2 * cellWidth}`}
              y1="0"
              x2="0"
              y2={`${2 * cellHeight}`}
              stroke="#8B7355"
              strokeWidth="2"
              strokeDasharray="6,4"
            />
          </svg>

          {/* 楚河汉界区域 */}
          <div
            className="absolute bg-amber-100 flex items-center justify-center border-t-3 border-b-3 border-amber-800"
            style={{
              top: `${margin + 4 * cellHeight}px`,
              left: `${margin}px`,
              width: `${boardWidth - 2 * margin}px`,
              height: `${cellHeight}px`
            }}
          >
            <div className="flex justify-around w-full px-8">
              <div className="text-2xl font-bold text-amber-900 font-serif tracking-widest">楚 河</div>
              <div className="text-2xl font-bold text-amber-900 font-serif tracking-widest">汉 界</div>
            </div>
          </div>

          
          {/* 所有交叉点的点击区域 - 优化触摸交互 */}
          {board.map((row, rowIndex) =>
            row.map((cell, colIndex) => {
              const isSelected = gameState.selectedPiece?.row === rowIndex && gameState.selectedPiece?.col === colIndex;
              const isLastMove = lastMovePos?.row === rowIndex && lastMovePos?.col === colIndex;

              return (
                <div
                  key={`click-${rowIndex}-${colIndex}`}
                  className={cn(
                    "absolute flex items-center justify-center transition-all duration-200",
                    "touch-manipulation", // 优化触摸性能
                    {
                      "cursor-pointer hover:scale-105": !disabled,
                      "cursor-not-allowed opacity-90": disabled,
                      "z-20": isSelected || isLastMove,
                      "z-10": !isSelected && !isLastMove
                    }
                  )}
                  style={{
                    top: `${margin + rowIndex * cellHeight - cellWidth/2}px`, // 精确居中对齐
                    left: `${margin + colIndex * cellWidth - cellWidth/2}px`,
                    width: `${cellWidth}px`,
                    height: `${cellHeight}px`,
                    touchAction: 'manipulation', // 防止双击缩放，只允许滚动和缩放手势
                  }}
                  onClick={() => {
                    if (disabled) return;
                    onCellClick({ row: rowIndex, col: colIndex });
                  }}
                >
                  {/* 棋子 */}
                  {cell && renderPiece(cell, rowIndex, colIndex)}

                  {/* 选中状态 - 增强视觉效果 */}
                  {isSelected && (
                    <>
                      <div className="absolute inset-0 rounded-full border-4 border-yellow-400 bg-yellow-200 opacity-70 animate-pulse"></div>
                      <div className="absolute inset-2 rounded-full border-2 border-yellow-500 opacity-80"></div>
                    </>
                  )}

                  {/* 最后一步标记 */}
                  {isLastMove && !isSelected && (
                    <div className="absolute -inset-1 rounded-full border-2 border-red-500 bg-red-500 opacity-25"></div>
                  )}

                  {/* 触摸反馈 */}
                  <div className="absolute inset-0 rounded-full opacity-0 hover:opacity-10 bg-white transition-opacity"></div>
                </div>
              );
            })
          )}

          {/* 可移动位置标记 - 优化显示 */}
          {gameState.possibleMoves?.map((move, index) => {
            const isCapture = board[move.to.row][move.to.col] !== null;
            const markerSize = Math.max(cellWidth / 3, 12); // 确保最小尺寸

            return (
              <div
                key={`move-${index}`}
                className="absolute flex items-center justify-center pointer-events-none animate-bounce"
                style={{
                  top: `${margin + move.to.row * cellHeight - markerSize/2}px`,
                  left: `${margin + move.to.col * cellWidth - markerSize/2}px`,
                  width: `${markerSize}px`,
                  height: `${markerSize}px`,
                  zIndex: 25
                }}
              >
                <div className={cn(
                  "w-full h-full rounded-full border-2 shadow-lg",
                  isCapture
                    ? "bg-red-500 border-red-700 animate-pulse" // 可以吃子
                    : "bg-green-500 border-green-700" // 空位移动
                )}>
                  {/* 内部高光 */}
                  <div className="absolute top-1 left-1 w-2 h-2 bg-white rounded-full opacity-60"></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 被吃掉的棋子显示 - 响应式布局 */}
      <div className="flex flex-col sm:flex-row justify-between w-full max-w-4xl px-4 gap-4">
        <div className="flex flex-col items-center">
          <div className="text-sm font-semibold text-gray-600 mb-2">黑方被吃</div>
          <div className="flex flex-wrap gap-1 justify-center max-w-xs">
            {gameState.capturedPieces.black.length > 0 ? (
              gameState.capturedPieces.black.map((piece, index) => (
                <div
                  key={index}
                  className={cn(
                    isZoomed ? "w-8 h-8 text-sm" : "w-6 h-6 text-xs sm:w-7 sm:h-7 sm:text-xs",
                    "rounded-full bg-gray-200 flex items-center justify-center shadow-sm border border-gray-300"
                  )}
                >
                  {XIANGQI_PIECE_SYMBOLS[piece.type]['black']}
                </div>
              ))
            ) : (
              <div className="text-xs text-gray-400">暂无被吃棋子</div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div className="text-sm font-semibold text-gray-600 mb-2">红方被吃</div>
          <div className="flex flex-wrap gap-1 justify-center max-w-xs">
            {gameState.capturedPieces.red.length > 0 ? (
              gameState.capturedPieces.red.map((piece, index) => (
                <div
                  key={index}
                  className={cn(
                    isZoomed ? "w-8 h-8 text-sm" : "w-6 h-6 text-xs sm:w-7 sm:h-7 sm:text-xs",
                    "rounded-full bg-red-200 flex items-center justify-center shadow-sm border border-red-300"
                  )}
                >
                  {XIANGQI_PIECE_SYMBOLS[piece.type]['red']}
                </div>
              ))
            ) : (
              <div className="text-xs text-gray-400">暂无被吃棋子</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default XiangqiBoard;